import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Loader2, X, ChevronRight, Flame, TrendingUp, ExternalLink,
  Star, Rocket, ArrowRight, Wand2, Zap, Target
} from "lucide-react";

function Chip({ children, onRemove, active, onClick, testId }) {
  return (
    <span
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[12px] border cursor-pointer transition-colors ${
        active ? "bg-white text-black border-white" : "border-white/15 text-zinc-300 hover:border-white/40"
      }`}
    >
      {children}
      {onRemove && (
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className="hover:text-[#FF3B30]">
          <X className="w-3 h-3"/>
        </button>
      )}
    </span>
  );
}

function RatingBar({ label, value, color = "bg-white" }) {
  const pct = Math.round((value || 0) * 100);
  return (
    <div>
      <div className="flex items-center justify-between mb-0.5">
        <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</span>
        <span className="font-mono text-[10px] text-zinc-300">{pct}</span>
      </div>
      <div className="h-1 bg-[#1a1a1c]">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }}/>
      </div>
    </div>
  );
}

export default function Orchestrate() {
  const qc = useQueryClient();
  const nav = useNavigate();

  const [mode, setMode] = useState("create"); // "create" | "join"
  const [keywordInput, setKeywordInput] = useState("");
  const [keywords, setKeywords] = useState([]);
  const [selectedSubs, setSelectedSubs] = useState(new Set());
  const [count, setCount] = useState(20);
  const [contextNote, setContextNote] = useState("");
  const [topics, setTopics] = useState([]);
  const [pickedTopics, setPickedTopics] = useState(new Set());
  const [creating, setCreating] = useState(false);
  const [trendSource, setTrendSource] = useState("reddit"); // reddit | google
  const [trendSubreddit, setTrendSubreddit] = useState("r/all");
  const [trendResult, setTrendResult] = useState(null);
  const [productId, setProductId] = useState("");
  const [selectedCompetitors, setSelectedCompetitors] = useState(new Set());
  const [speed, setSpeed] = useState("balanced");
  const [singleSubLock, setSingleSubLock] = useState(false);
  const [lockedSub, setLockedSub] = useState("");
  const [targetGroupId, setTargetGroupId] = useState("");
  const [pickedMixes, setPickedMixes] = useState({}); // { mix_id: count }
  const [mixSearch, setMixSearch] = useState("");

  const groupsQ = useQuery({
    queryKey: ["agent-groups"],
    queryFn: () => api.get("/agent-groups").then(r => r.data),
  });
  const groups = groupsQ.data || [];

  const mixesQ = useQuery({
    queryKey: ["awareness-mixes"],
    queryFn: () => api.get("/awareness-mixes?enabled_only=true").then(r => r.data),
  });
  const mixes = mixesQ.data || [];

  // ----- Campaign templates -----
  const templatesQ = useQuery({
    queryKey: ["campaign-templates"],
    queryFn: () => api.get("/campaign-templates").then(r => r.data),
  });
  const templates = templatesQ.data || [];
  const [showSaveTpl, setShowSaveTpl] = useState(false);
  const [tplName, setTplName] = useState("");

  const saveTpl = useMutation({
    mutationFn: () => api.post("/campaign-templates", {
      name: tplName,
      group_id: targetGroupId || null,
      speed,
      product_id: productId || null,
      competitor_ids: [...selectedCompetitors],
      force_subreddit: singleSubLock && lockedSub ? lockedSub : null,
      angle_selection: Object.entries(pickedMixes).map(([mid, c]) => ({ mix_id: parseInt(mid, 10), count: c })),
      default_num_agents: null,
      default_objective: contextNote || null,
    }),
    onSuccess: () => {
      toast.success(`Saved template "${tplName}"`);
      setShowSaveTpl(false); setTplName("");
      qc.invalidateQueries({ queryKey: ["campaign-templates"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const applyTemplate = (tpl) => {
    if (!tpl) return;
    if (tpl.group_id != null) setTargetGroupId(tpl.group_id || "");
    if (tpl.speed) setSpeed(tpl.speed);
    if (tpl.product_id != null) setProductId(tpl.product_id || "");
    if (Array.isArray(tpl.competitor_ids)) setSelectedCompetitors(new Set(tpl.competitor_ids));
    if (tpl.force_subreddit) { setSingleSubLock(true); setLockedSub(tpl.force_subreddit); }
    if (Array.isArray(tpl.angle_selection)) {
      const picked = {};
      tpl.angle_selection.forEach(s => { picked[s.mix_id] = s.count || 1; });
      setPickedMixes(picked);
    }
    if (tpl.default_objective) setContextNote(tpl.default_objective);
    toast.success(`Applied template "${tpl.name}"`);
  };

  const delTpl = useMutation({
    mutationFn: (tid) => api.delete(`/campaign-templates/${tid}`),
    onSuccess: () => { toast.success("Template deleted"); qc.invalidateQueries({ queryKey: ["campaign-templates"] }); },
  });

  const subsQ = useQuery({
    queryKey: ["subreddits", productId || "all"],
    queryFn: () => api.get("/subreddits", { params: productId ? { product_id: productId } : {} }).then(r => r.data),
  });
  const subs = subsQ.data || [];

  const productsQ = useQuery({
    queryKey: ["products"],
    queryFn: () => api.get("/products").then(r => r.data),
  });
  const products = productsQ.data || [];

  const competitorsQ = useQuery({
    queryKey: ["competitors", productId || "all"],
    queryFn: () => api.get("/competitors", { params: productId ? { product_id: productId } : {} }).then(r => r.data),
  });
  const competitors = competitorsQ.data || [];

  const suggest = useMutation({
    mutationFn: () => api.post("/topics/suggest", {
      keywords,
      subreddit_ids: singleSubLock ? [] : [...selectedSubs],
      count, context_note: contextNote || null,
      product_id: productId || null,
      competitor_ids: [...selectedCompetitors],
      speed,
      force_subreddit: singleSubLock && lockedSub ? lockedSub : null,
      angle_selection: Object.entries(pickedMixes).map(([mid, c]) => ({ mix_id: parseInt(mid, 10), count: c })),
    }, { timeout: 120000 }),
    onSuccess: (r) => { setTopics(r.data.topics); toast.success(`Generated ${r.data.count} topics`); },
    onError: (e) => {
      const detail = String(e?.response?.data?.detail || "");
      const lower = detail.toLowerCase();
      if (lower.includes("quota") || lower.includes("resource_exhausted") || lower.includes("limit: 0")) {
        toast.error("Gemini quota is unavailable for this key. Add billing or set a working Gemini key in AI Settings.");
      } else if (lower.includes("503") || lower.includes("high demand") || lower.includes("unavailable")) {
        toast.error("Gemini is temporarily busy. Please retry in a few seconds.");
      } else {
        toast.error(detail || "Generation failed");
      }
    },
  });

  const reddit = useMutation({
    mutationFn: (body) => api.post("/reddit/trending", body),
    onSuccess: (r) => setTrendResult({ kind: "reddit", ...r.data }),
    onError: (e) => toast.error(e?.response?.data?.detail || "Reddit fetch failed"),
  });

  const google = useMutation({
    mutationFn: (body) => api.post("/trends/google", body, { timeout: 90000 }),
    onSuccess: (r) => setTrendResult({ kind: "google", ...r.data }),
    onError: (e) => toast.error(e?.response?.data?.detail || "Google Trends fetch failed"),
  });

  const createCampaign = async (topicId) => {
    setCreating(true);
    try {
      const r = await api.post(`/topics/${topicId}/create-campaign`, {
        product_id: productId || null,
        competitor_ids: [...selectedCompetitors],
        speed,
        force_subreddit: singleSubLock && lockedSub ? lockedSub : null,
        group_id: targetGroupId || null,
        angle_selection: Object.entries(pickedMixes).map(([mid, c]) => ({ mix_id: parseInt(mid, 10), count: c })),
      }, { timeout: 30000 });
      return r.data;
    } finally {
      setCreating(false);
    }
  };

  const addKeyword = () => {
    const kw = keywordInput.trim();
    if (!kw) return;
    if (keywords.includes(kw)) { setKeywordInput(""); return; }
    setKeywords([...keywords, kw]);
    setKeywordInput("");
  };

  const runReddit = () => reddit.mutate({ subreddit: trendSubreddit, section: "hot", limit: 15 });
  const runGoogle = () => {
    if (!keywords.length) { toast.error("Add at least one keyword first"); return; }
    google.mutate({ keywords: keywords.slice(0, 5), geo: "" });
  };

  const generate = () => {
    if (!keywords.length) { toast.error("Add at least one keyword"); return; }
    setTopics([]); setPickedTopics(new Set());
    suggest.mutate();
  };

  const createSelected = async () => {
    if (!pickedTopics.size) { toast.error("Select at least one topic"); return; }
    const list = [...pickedTopics];
    toast.info(`Creating ${list.length} campaign(s)…`);
    const results = [];
    for (const id of list) {
      try {
        const r = await createCampaign(id);
        results.push(r);
      } catch (e) {
        toast.error(`Topic ${id.slice(0,6)}: ${e?.response?.data?.detail || "failed"}`);
      }
    }
    if (results.length) {
      toast.success(`${results.length} campaign(s) started — you can leave this tab, generation runs in the background.`);
      qc.invalidateQueries({ queryKey: ["threads"] });
      qc.invalidateQueries({ queryKey: ["admin-stats"] });
      // If exactly one, jump into it
      if (results.length === 1) {
        nav(`/admin/threads/${results[0].thread_id}`);
      } else {
        nav("/admin?filter=Orchestrating");
      }
    }
  };

  const sortedTopics = useMemo(
    () => [...topics].sort((a, b) => (b.ratings?.overall || 0) - (a.ratings?.overall || 0)),
    [topics]
  );

  return (
    <Layout
      title="Orchestrate."
      subtitle="AI-driven campaign genesis"
      right={
        <div className="text-right">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">JARVIS · Google Gemini</div>
          <div className="font-mono text-[11px] text-zinc-300 flex items-center justify-end gap-1"><Zap className="w-3 h-3 text-[#FF3B30]"/>ready</div>
        </div>
      }
    >
      {/* MODE SWITCHER */}
      <div className="mb-6 flex flex-col sm:flex-row gap-2" data-testid="orchestrate-mode-tabs">
        <button
          onClick={() => setMode("create")}
          data-testid="mode-create-tab"
          className={`flex-1 p-4 border text-left transition-colors ${mode === "create" ? "border-[var(--accent)] bg-[var(--accent-soft)]" : "border-[var(--border)] hover:border-[var(--border-strong)]"}`}
        >
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className={`w-4 h-4 ${mode === "create" ? "text-[var(--accent)]" : "text-[var(--text-muted)]"}`}/>
            <span className="font-mono text-[10px] uppercase tracking-widest">Mode A</span>
          </div>
          <div className="font-display font-black text-lg tracking-tight">Create new post.</div>
          <div className="text-[11px] text-[var(--text-muted)] mt-1 leading-relaxed">
            Jarvis proposes topics. One of your agents becomes the OP and creates the seed post on Reddit.
          </div>
        </button>
        <button
          onClick={() => setMode("join")}
          data-testid="mode-join-tab"
          className={`flex-1 p-4 border text-left transition-colors ${mode === "join" ? "border-[var(--accent)] bg-[var(--accent-soft)]" : "border-[var(--border)] hover:border-[var(--border-strong)]"}`}
        >
          <div className="flex items-center gap-2 mb-1">
            <Target className={`w-4 h-4 ${mode === "join" ? "text-[var(--accent)]" : "text-[var(--text-muted)]"}`}/>
            <span className="font-mono text-[10px] uppercase tracking-widest">Mode B</span>
          </div>
          <div className="font-display font-black text-lg tracking-tight">Join existing thread.</div>
          <div className="text-[11px] text-[var(--text-muted)] mt-1 leading-relaxed">
            Paste a Reddit thread posted by a real user. Your agents will engage it — no OP task, everyone targets the pasted URL.
          </div>
        </button>
      </div>

      {mode === "join" && <JoinExistingThread nav={nav}/>}
      {mode === "create" && (
      <div className="grid xl:grid-cols-3 gap-6 min-w-0">
        {/* LEFT: Inputs */}
        <div className="xl:col-span-1 space-y-4 min-w-0">
          {/* Keywords */}
          <div className="border border-white/10 p-5">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">// step 01</div>
            <h3 className="font-display font-black text-xl tracking-tight mb-4">Target keywords.</h3>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                placeholder="e.g. organic fertilizer"
                value={keywordInput}
                onChange={(e)=>setKeywordInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addKeyword())}
                data-testid="keyword-input"
                className="flex-1 bg-[#050505] border border-white/10 px-3 py-2 text-[13px] font-mono"
              />
              <button
                onClick={addKeyword}
                data-testid="add-keyword-btn"
                className="border border-white/15 hover:border-white/40 px-3 text-[12px]"
              >
                ADD
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5 min-h-[2rem]">
              {keywords.map((k) => (
                <Chip key={k} onRemove={() => setKeywords(keywords.filter(x => x !== k))} testId={`kw-chip-${k}`}>
                  {k}
                </Chip>
              ))}
              {!keywords.length && <div className="text-[11px] font-mono text-zinc-600">— no keywords yet —</div>}
            </div>
          </div>

          {/* Subreddits */}
          <div className="border border-white/10 p-5">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">// step 02 · brand & subreddits</div>
            <h3 className="font-display font-black text-xl tracking-tight mb-4">Brand + targets.</h3>

            <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Promoting brand (optional)</label>
            <select
              value={productId}
              onChange={(e)=>setProductId(e.target.value)}
              data-testid="product-picker"
              className="w-full bg-[#050505] border border-white/10 px-3 py-2 mb-4 text-[13px]"
            >
              <option value="">— generic (no brand) —</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {productId && (
              <div className="mb-4 -mt-2 text-[11px] font-mono text-amber-400">
                Jarvis will subtly weave {products.find(p=>p.id===productId)?.name} into topics. The 'Closer' persona will drop your landing URL naturally.
              </div>
            )}

            <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Competitors to undermine (optional)</label>
            {competitors.length === 0 ? (
              <div className="text-[12px] text-zinc-500 leading-relaxed mb-4">
                No competitors tracked. <a href="/admin/competitors" className="text-[#FF3B30] hover:underline">Add some →</a>
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {competitors.map(c => (
                  <Chip
                    key={c.id}
                    active={selectedCompetitors.has(c.id)}
                    onClick={() => {
                      const n = new Set(selectedCompetitors);
                      n.has(c.id) ? n.delete(c.id) : n.add(c.id);
                      setSelectedCompetitors(n);
                    }}
                    testId={`competitor-chip-${c.id}`}
                  >
                    {c.name}
                  </Chip>
                ))}
              </div>
            )}
            {selectedCompetitors.size > 0 && (
              <div className="mb-4 -mt-2 text-[11px] font-mono text-[#FF3B30]">
                Weak spots of {selectedCompetitors.size} competitor(s) will be seeded as user frustration.
              </div>
            )}

            <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Speed profile</label>
            {/* Speed profile toggle */}
            <div className="mb-4 p-3 border border-[var(--border)] bg-[var(--bg-2)]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">// speed profile</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setSpeed("fast")}
                    data-testid="orch-speed-fast"
                    className={`px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest border ${speed === "fast" ? "border-[var(--accent)] text-[var(--accent)]" : "border-white/10 text-zinc-500 hover:text-white"}`}
                  >
                    ⚡ Fast
                  </button>
                  <button
                    onClick={() => setSpeed("balanced")}
                    data-testid="orch-speed-balanced"
                    className={`px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest border ${speed === "balanced" ? "border-emerald-400 text-emerald-400" : "border-white/10 text-zinc-500 hover:text-white"}`}
                  >
                    Balanced
                  </button>
                </div>
              </div>
              <div className="text-[10px] font-mono text-zinc-500">
                {speed === "fast" ? "Gemini Flash · 3-8s" : "Gemini Pro · 20-45s"}
              </div>
            </div>

            {/* Single-subreddit lock */}
            <div className="mb-4 p-3 border border-[var(--border)] bg-[var(--bg-2)]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">// single-subreddit lock</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={singleSubLock}
                    onChange={(e) => setSingleSubLock(e.target.checked)}
                    data-testid="orch-single-sub-toggle"
                    className="w-4 h-4 accent-[var(--accent)]"
                  />
                  <span className="text-[11px] font-mono uppercase tracking-widest">{singleSubLock ? "ON" : "OFF"}</span>
                </label>
              </div>
              {singleSubLock ? (
                <>
                  <div className="text-[10px] font-mono text-amber-400 mb-2">
                    Every campaign will post to the ONE subreddit below (overrides Jarvis' picks).
                  </div>
                  <select
                    value={lockedSub}
                    onChange={(e) => setLockedSub(e.target.value)}
                    data-testid="orch-locked-sub-select"
                    className="w-full bg-[#050505] border border-white/10 focus:border-[var(--accent)] px-2 py-2 text-[13px] text-zinc-200 font-mono"
                  >
                    <option value="">— pick one —</option>
                    {subs.map(s => (
                      <option key={s.id} value={s.name}>{s.name}{s.description ? ` — ${s.description.slice(0,40)}` : ""}</option>
                    ))}
                  </select>
                </>
              ) : (
                <div className="text-[10px] font-mono text-zinc-500">
                  Jarvis picks a fitting subreddit per topic from the candidates below.
                </div>
              )}
            </div>

            {/* Templates strip */}
            <div className="mb-4 p-3 border border-[var(--border)] bg-[var(--bg-2)]" data-testid="orch-templates-strip">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">// campaign templates</span>
                <button
                  onClick={() => setShowSaveTpl(!showSaveTpl)}
                  data-testid="orch-save-tpl-toggle"
                  className="text-[10px] font-mono uppercase tracking-widest text-[var(--accent)] hover:underline"
                >{showSaveTpl ? "cancel" : "+ save current"}</button>
              </div>
              {showSaveTpl && (
                <div className="flex gap-2 mb-2" data-testid="orch-save-tpl-form">
                  <input
                    value={tplName}
                    onChange={(e) => setTplName(e.target.value)}
                    placeholder="Template name (e.g. Mumbai fear campaign)"
                    data-testid="orch-tpl-name-input"
                    className="flex-1 bg-[#050505] border border-white/10 px-2 py-1.5 text-[12px]"
                  />
                  <button
                    onClick={() => { if (!tplName.trim()) return toast.error("Name required"); saveTpl.mutate(); }}
                    disabled={saveTpl.isPending}
                    data-testid="orch-save-tpl-btn"
                    className="bg-[var(--accent)] hover:opacity-90 text-white text-[11px] font-mono uppercase tracking-widest px-3 disabled:opacity-50"
                  >save</button>
                </div>
              )}
              {templates.length === 0 && !showSaveTpl && (
                <div className="text-[10px] font-mono text-zinc-600">No templates yet — set up a campaign then click "save current" to reuse it.</div>
              )}
              <div className="flex flex-wrap gap-1.5">
                {templates.map(t => (
                  <div key={t.id} className="flex items-center border border-white/10 hover:border-[var(--accent)]/60" data-testid={`orch-tpl-${t.id}`}>
                    <button
                      onClick={() => applyTemplate(t)}
                      className="text-[10px] font-mono px-2 py-1 text-zinc-200 hover:text-white"
                      data-testid={`orch-apply-tpl-${t.id}`}
                    >{t.name}</button>
                    <button
                      onClick={() => { if (window.confirm(`Delete template "${t.name}"?`)) delTpl.mutate(t.id); }}
                      className="px-1.5 py-1 text-zinc-500 hover:text-[#FF3B30] border-l border-white/10"
                      data-testid={`orch-del-tpl-${t.id}`}
                    >×</button>
                  </div>
                ))}
              </div>
            </div>

            {/* Target group */}
            <div className="mb-4 p-3 border border-[var(--border)] bg-[var(--bg-2)]">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">// target agents</div>
              <select
                value={targetGroupId}
                onChange={(e) => setTargetGroupId(e.target.value)}
                data-testid="orch-group-select"
                className="w-full bg-[#050505] border border-white/10 px-2 py-2 text-[13px]"
              >
                <option value="">All eligible agents (default)</option>
                {groups.map(g => (
                  <option key={g.id} value={g.id}>{g.name} ({g.agent_count})</option>
                ))}
              </select>
              {targetGroupId && (
                <div className="mt-2 text-[10px] font-mono text-amber-400">
                  Campaign tasks will ONLY go to this group's agents.
                </div>
              )}
              <div className="mt-2 text-[9px] font-mono text-zinc-600">
                Agents without a complete profile or still in the 7-day warming window will be skipped automatically.
              </div>
            </div>

            {/* Angle selector */}
            <div className="mb-4 p-3 border border-[var(--border)] bg-[var(--bg-2)]" data-testid="orch-angle-selector">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">// awareness angles</span>
                <span className="text-[10px] font-mono text-zinc-500">
                  {Object.values(pickedMixes).reduce((a, b) => a + b, 0)} picked
                </span>
              </div>
              <input
                type="text"
                placeholder="search mixes (family/vibe)…"
                value={mixSearch}
                onChange={(e) => setMixSearch(e.target.value)}
                data-testid="orch-mix-search"
                className="w-full bg-[#050505] border border-white/10 px-2 py-1.5 text-[11px] font-mono mb-2"
              />
              <div className="max-h-[220px] overflow-y-auto space-y-1">
                {(mixes || [])
                  .filter(m => {
                    if (!mixSearch) return true;
                    const q = mixSearch.toLowerCase();
                    return (
                      (m.family || "").toLowerCase().includes(q) ||
                      (m.angle || "").toLowerCase().includes(q) ||
                      (m.vibe || "").toLowerCase().includes(q)
                    );
                  })
                  .slice(0, 30)
                  .map(m => {
                    const cnt = pickedMixes[m.mix_id] || 0;
                    return (
                      <div key={m.id} className={`flex items-start gap-2 p-2 border text-[11px] ${cnt > 0 ? "border-[#FF3B30]/40 bg-[#FF3B30]/5" : "border-white/5"}`} data-testid={`orch-mix-row-${m.mix_id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-[10px] text-zinc-500">#{m.mix_id} · {m.family} → {m.angle}</div>
                          <div className="text-[11px] text-zinc-300 truncate">{m.vibe}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              const next = { ...pickedMixes };
                              if (next[m.mix_id]) delete next[m.mix_id];
                              setPickedMixes(next);
                            }}
                            disabled={!cnt}
                            data-testid={`orch-mix-clear-${m.mix_id}`}
                            className="w-6 h-6 border border-white/10 hover:border-white/30 disabled:opacity-30 text-[10px]"
                          >×</button>
                          <span className="w-6 text-center font-mono text-[11px]">{cnt}</span>
                          <button
                            onClick={() => setPickedMixes({ ...pickedMixes, [m.mix_id]: (cnt + 1) })}
                            data-testid={`orch-mix-add-${m.mix_id}`}
                            className="w-6 h-6 border border-white/15 hover:border-[#FF3B30] text-[11px] font-mono"
                          >+</button>
                        </div>
                      </div>
                    );
                  })}
                {(mixes || []).length === 0 && (
                  <div className="text-[11px] text-zinc-500 py-4 text-center italic">No mixes loaded yet.</div>
                )}
              </div>
              <div className="mt-2 text-[9px] font-mono text-zinc-600">
                Pick specific persuasion angles + how many examples per angle. Empty selection ⇒ Jarvis picks 3 at random.
              </div>
            </div>

            <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Candidate subreddits</label>
            {subs.length === 0 ? (
              <div className="text-[12px] text-zinc-500 leading-relaxed">
                No subreddits saved. <a href="/admin/subreddits" className="text-[#FF3B30] hover:underline">Add some →</a>
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5 max-h-56 overflow-y-auto">
                {subs.map(s => (
                  <Chip
                    key={s.id}
                    active={selectedSubs.has(s.id)}
                    onClick={() => {
                      const n = new Set(selectedSubs);
                      n.has(s.id) ? n.delete(s.id) : n.add(s.id);
                      setSelectedSubs(n);
                    }}
                    testId={`sub-chip-${s.id}`}
                  >
                    {s.name}
                  </Chip>
                ))}
              </div>
            )}
          </div>

          {/* Trending peek */}
          <div className="border border-white/10 p-5">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">// step 03 (optional)</div>
            <h3 className="font-display font-black text-xl tracking-tight mb-3">Peek real-time trends.</h3>
            <div className="flex gap-1 mb-3">
              <button
                onClick={() => setTrendSource("reddit")}
                data-testid="trend-source-reddit"
                className={`flex-1 px-2 py-1 text-[11px] font-mono uppercase tracking-widest border ${
                  trendSource === "reddit" ? "bg-white text-black border-white" : "border-white/10 text-zinc-400"
                }`}
              >Reddit</button>
              <button
                onClick={() => setTrendSource("google")}
                data-testid="trend-source-google"
                className={`flex-1 px-2 py-1 text-[11px] font-mono uppercase tracking-widest border ${
                  trendSource === "google" ? "bg-white text-black border-white" : "border-white/10 text-zinc-400"
                }`}
              >Google</button>
            </div>
            {trendSource === "reddit" ? (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={trendSubreddit}
                  onChange={(e)=>setTrendSubreddit(e.target.value)}
                  placeholder="r/gardening or r/all"
                  data-testid="reddit-trend-sub"
                  className="flex-1 bg-[#050505] border border-white/10 px-2 py-1.5 text-[12px] font-mono"
                />
                <button
                  onClick={runReddit}
                  disabled={reddit.isPending}
                  data-testid="run-reddit-btn"
                  className="border border-white/15 hover:border-white/40 px-3 text-[12px] flex items-center gap-1 disabled:opacity-50"
                >
                  {reddit.isPending ? <Loader2 className="w-3 h-3 animate-spin"/> : <Flame className="w-3 h-3"/>}
                  FETCH
                </button>
              </div>
            ) : (
              <button
                onClick={runGoogle}
                disabled={google.isPending}
                data-testid="run-google-btn"
                className="w-full border border-white/15 hover:border-white/40 py-2 text-[12px] flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {google.isPending ? <Loader2 className="w-3 h-3 animate-spin"/> : <TrendingUp className="w-3 h-3"/>}
                CHECK GOOGLE TRENDS (uses keywords above)
              </button>
            )}
            {trendResult && <TrendResultPanel data={trendResult} onAddKeyword={(k) => !keywords.includes(k) && setKeywords([...keywords, k])} />}
          </div>

          {/* Config + Generate */}
          <div className="border border-[#FF3B30]/40 p-5 bg-gradient-to-b from-[#0a0a0c] to-[#050505]">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF3B30] mb-2">// launch</div>
            <h3 className="font-display font-black text-xl tracking-tight mb-4">Generate topics.</h3>

            <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Topic count</label>
            <input
              type="number" min={5} max={30} value={count}
              onChange={(e) => setCount(parseInt(e.target.value || "20", 10))}
              data-testid="count-input"
              className="w-full bg-[#050505] border border-white/10 px-3 py-2 mb-4 text-sm font-mono"
            />

            <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Additional guidance (optional)</label>
            <textarea
              rows={3}
              value={contextNote}
              onChange={(e) => setContextNote(e.target.value)}
              placeholder="e.g. focus on Indian market, urban gardeners…"
              data-testid="context-input"
              className="w-full bg-[#050505] border border-white/10 px-3 py-2 mb-4 text-[13px]"
            />

            <button
              onClick={generate}
              disabled={suggest.isPending || !keywords.length}
              data-testid="generate-topics-btn"
              className="w-full bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium py-3 flex items-center justify-center gap-2 disabled:opacity-40"
            >
              {suggest.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Wand2 className="w-4 h-4"/>}
              {suggest.isPending ? "JARVIS IS THINKING…" : "ASK JARVIS"}
            </button>
          </div>
        </div>

        {/* RIGHT: Topic gallery */}
        <div className="xl:col-span-2 min-w-0">
          <div className="border border-white/10 min-h-[600px] min-w-0 overflow-hidden">
            <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">// topic gallery</div>
                <div className="text-[13px] text-zinc-100 mt-0.5">
                  {topics.length ? `${topics.length} ideas · ${pickedTopics.size} selected` : "AI-generated ideas will appear here"}
                </div>
              </div>
              {topics.length > 0 && (
                <button
                  onClick={createSelected}
                  disabled={creating || !pickedTopics.size}
                  data-testid="create-campaigns-btn"
                  className="bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium px-4 py-2 text-[13px] flex items-center gap-2 disabled:opacity-40"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin"/> : <Rocket className="w-4 h-4"/>}
                  CREATE {pickedTopics.size || ""} CAMPAIGN{pickedTopics.size !== 1 ? "S" : ""}
                </button>
              )}
            </div>

            {suggest.isPending && (
              <div className="p-16 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#FF3B30] mx-auto mb-4"/>
                <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">// jarvis is composing 20+ authentic Reddit posts</div>
                <div className="text-zinc-400 text-sm mt-1">This typically takes 20-40s.</div>
              </div>
            )}

            {!suggest.isPending && !topics.length && (
              <div className="p-16 text-center bg-dots">
                <Sparkles className="w-8 h-8 text-zinc-700 mx-auto mb-4"/>
                <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-1">// awaiting mission</div>
                <div className="text-zinc-400 text-sm">Add keywords, optionally pick candidate subreddits, then hit <b>Ask Jarvis</b>.</div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-0">
              {sortedTopics.map((t) => (
                <TopicCard
                  key={t.id}
                  topic={t}
                  picked={pickedTopics.has(t.id)}
                  onToggle={() => {
                    const n = new Set(pickedTopics);
                    n.has(t.id) ? n.delete(t.id) : n.add(t.id);
                    setPickedTopics(n);
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      )}
    </Layout>
  );
}

function JoinExistingThread({ nav }) {
  const [form, setForm] = useState({ reddit_url: "", subreddit: "", title: "", body: "", author: "", intent_note: "" });
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const create = useMutation({
    mutationFn: () => api.post("/threads/manual", form),
    onSuccess: (r) => {
      toast.success("Thread saved — orchestrate to compose the campaign");
      nav(`/admin/threads/${r.data.thread.id}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  // Auto-detect subreddit from URL
  React.useEffect(() => {
    if (form.reddit_url && !form.subreddit) {
      const m = form.reddit_url.match(/reddit\.com\/r\/([A-Za-z0-9_]+)/);
      if (m) setForm(p => ({ ...p, subreddit: `r/${m[1]}` }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.reddit_url]);
  const canSubmit = form.reddit_url.trim() && form.title.trim();
  return (
    <div className="max-w-3xl border border-[var(--border)] bg-[var(--surface)] p-5 sm:p-8">
      <div className="mb-6">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">// join a real user's thread</div>
        <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight mt-1">Paste the thread.</h3>
        <p className="text-[12px] text-[var(--text-muted)] mt-2 leading-relaxed">
          Copy the Reddit URL of the post you want your agents to engage with, plus its title and (optional) body. Jarvis will build a Comment / Reply / Upvote campaign that targets THIS exact thread — no OP task, agents start engaging as soon as you deploy.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Reddit URL *</label>
          <input
            type="url"
            placeholder="https://www.reddit.com/r/india/comments/xxx/why_is_wfh_so_bad_now/"
            value={form.reddit_url}
            onChange={set("reddit_url")}
            data-testid="join-thread-url"
            className="w-full bg-[var(--input-bg)] border border-[var(--border)] focus:border-[var(--border-strong)] focus:outline-none px-3 py-2 text-[13px] font-mono"
          />
          <div className="text-[10px] font-mono text-[var(--text-muted)] mt-1">We auto-detect the subreddit from this URL.</div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Subreddit</label>
            <input value={form.subreddit} onChange={set("subreddit")} placeholder="r/india" data-testid="join-thread-sub"
              className="w-full bg-[var(--input-bg)] border border-[var(--border)] px-3 py-2 text-[13px] font-mono"/>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">OP username (optional)</label>
            <input value={form.author} onChange={set("author")} placeholder="u/original_poster" data-testid="join-thread-author"
              className="w-full bg-[var(--input-bg)] border border-[var(--border)] px-3 py-2 text-[13px] font-mono"/>
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Original title *</label>
          <input value={form.title} onChange={set("title")} placeholder="Copy the exact post title as it appears on Reddit" data-testid="join-thread-title"
            className="w-full bg-[var(--input-bg)] border border-[var(--border)] focus:border-[var(--border-strong)] focus:outline-none px-3 py-2 text-[13px]"/>
        </div>

        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Original body / description</label>
          <textarea rows={6} value={form.body} onChange={set("body")}
            placeholder="Paste what the original user wrote in their post body (the full text below the title). Include typos and slang — helps Jarvis match the tone."
            data-testid="join-thread-body"
            className="w-full bg-[var(--input-bg)] border border-[var(--border)] focus:border-[var(--border-strong)] focus:outline-none px-3 py-2 text-[13px]"/>
        </div>

        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Campaign intent (optional)</label>
          <input value={form.intent_note} onChange={set("intent_note")}
            placeholder="What's your goal here? e.g. seed leads for our SaaS · softly attack CompeteX"
            data-testid="join-thread-intent"
            className="w-full bg-[var(--input-bg)] border border-[var(--border)] px-3 py-2 text-[13px]"/>
        </div>

        <button
          onClick={() => create.mutate()}
          disabled={create.isPending || !canSubmit}
          data-testid="join-thread-submit"
          className="w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white font-medium py-3 flex items-center justify-center gap-2 disabled:opacity-40"
        >
          {create.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Rocket className="w-4 h-4"/>}
          Save &amp; orchestrate this thread
        </button>
        <div className="text-[10px] font-mono text-[var(--text-muted)] text-center">
          You'll land on the thread detail page where you can pick a Brand, Competitors, and hit Orchestrate.
        </div>
      </div>
    </div>
  );
}

function TopicCard({ topic, picked, onToggle }) {
  const overall = topic.ratings?.overall || 0;
  return (
    <div
      onClick={onToggle}
      data-testid={`topic-card-${topic.id}`}
      className={`p-5 border-b border-r border-white/5 cursor-pointer transition-all ${
        picked ? "bg-[#141416] ring-1 ring-inset ring-[#FF3B30]" : "hover:bg-[#0e0e10]"
      }`}
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="font-mono text-[10px] text-[#FF3B30]">{topic.target_subreddit}</span>
        <span className="text-zinc-700">·</span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{topic.angle}</span>
        <div className="ml-auto flex items-center gap-1">
          <Star className={`w-3 h-3 ${overall > 0.7 ? "text-amber-400 fill-amber-400" : "text-zinc-600"}`}/>
          <span className="font-mono text-[11px] text-zinc-300">{overall.toFixed(2)}</span>
        </div>
      </div>
      <div className="text-[14px] font-medium text-zinc-100 mb-2 line-clamp-2">{topic.title}</div>
      <div className="text-[12px] text-zinc-400 line-clamp-3 mb-3 min-h-[3.5em]">{topic.body}</div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <RatingBar label="Viral" value={topic.ratings?.viral} color="bg-[#FF3B30]"/>
        <RatingBar label="Intent" value={topic.ratings?.intent} color="bg-emerald-400"/>
        <RatingBar label="Effort" value={topic.ratings?.effort} color="bg-amber-400"/>
        <RatingBar label="Risk" value={topic.ratings?.risk} color="bg-[#007AFF]"/>
      </div>

      {topic.rationale && (
        <div className="font-mono text-[10px] text-zinc-500 border-t border-white/5 pt-2 leading-relaxed">
          // {topic.rationale}
        </div>
      )}

      <div className="flex items-center gap-1.5 mt-3 flex-wrap">
        {(topic.keywords_used || []).slice(0, 6).map((k, i) => (
          <span key={`kw-${i}-${k}`} className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 border border-white/10 px-1.5 py-0.5">{k}</span>
        ))}
      </div>

      <div className={`mt-3 pt-3 border-t border-white/5 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest ${picked ? "text-[#FF3B30]" : "text-zinc-500"}`}>
        <span>{picked ? "◉ selected" : "◯ click to select"}</span>
        {picked && <ArrowRight className="w-3 h-3"/>}
      </div>
    </div>
  );
}

function TrendResultPanel({ data, onAddKeyword }) {
  if (data.kind === "reddit") {
    return (
      <div className="mt-4 bg-[#050505] border border-white/5 max-h-96 overflow-y-auto" data-testid="reddit-trend-result">
        <div className="px-3 py-2 border-b border-white/5 font-mono text-[10px] uppercase tracking-widest text-zinc-500 sticky top-0 bg-[#050505]">
          {data.subreddit} · {data.section} ({data.posts?.length || 0})
        </div>
        {data.posts?.slice(0, 12).map((p) => (
          <a key={p.id} href={p.url} target="_blank" rel="noreferrer" className="block px-3 py-2 hover:bg-[#0e0e10] border-b border-white/5">
            <div className="text-[12px] text-zinc-200 line-clamp-2 mb-1">{p.title}</div>
            <div className="font-mono text-[10px] text-zinc-500 flex items-center gap-2">
              <span>↑{p.score}</span>
              <span>💬{p.num_comments}</span>
              <span className="text-[#FF3B30]">{p.subreddit}</span>
              <ExternalLink className="w-2.5 h-2.5 ml-auto"/>
            </div>
          </a>
        ))}
      </div>
    );
  }
  // google
  return (
    <div className="mt-4 bg-[#050505] border border-white/5 p-3 max-h-96 overflow-y-auto" data-testid="google-trend-result">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
        Google Trends · {data.keywords?.join(" vs ")}
      </div>
      {Object.entries(data.related || {}).map(([kw, groups]) => (
        <div key={kw} className="mb-3">
          <div className="text-[11px] text-zinc-200 mb-1"><b>{kw}</b> — rising queries</div>
          <div className="flex flex-wrap gap-1">
            {(groups.rising || []).slice(0, 8).map((r, i) => (
              <button key={`rec-${r.query || i}-${i}`} onClick={() => onAddKeyword(r.query)} title="Add as keyword"
                className="font-mono text-[10px] text-emerald-300 border border-emerald-500/30 hover:border-emerald-500 px-1.5 py-0.5">
                +{r.query}
              </button>
            ))}
            {!(groups.rising || []).length && <span className="text-[10px] text-zinc-600">no rising queries</span>}
          </div>
        </div>
      ))}
    </div>
  );
}
